import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { pluralizeToString } from 'lib/string/pluralize'
import type { MouseEventHandler } from 'react'
import React, { useCallback } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { generateDurations } from 'selectors/offerPage/bedbankOfferPageSelectors'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Label from 'LuxKit/Label'
import Caption from 'LuxKit/Typography/Caption'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import Group from 'components/utils/Group'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

interface Props {
  checkIn?: string
  checkOut?: string
  duration: number
  durationOptions: Array<App.BedbankDurationOption>
  rooms?: Array<App.Occupants>
  adultsNumber: number
  displayPricingAsPerPerson: boolean
  flights: boolean
  onClose?: () => void
}

function getSubtitleLabel(option: App.BedbankDurationOption) {
  return option.hasPromo && <LabelGroup>
    <Label
      startIcon={<SolidFireIcon colour="urgency" size="XS" />}
      variant="critical"
      kind="plain"
    >
      Limited Time Special
    </Label>
  </LabelGroup>
}

function CalendarDurationDropdown(props: Props) {
  const { onClose, duration, checkIn, checkOut, durationOptions, rooms, adultsNumber, displayPricingAsPerPerson, flights } = props
  const dispatch = useOfferPageDispatch()

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const duration = parseInt(event.currentTarget.dataset.duration!, 10)

    if (checkIn && checkOut) {
      const checkOut = moment(checkIn).add(duration, 'days')
      dispatch({ type: OfferPageActions.setCheckOutDate, checkOut: checkOut.format(ISO_DATE_FORMAT) })
    }

    dispatch({ type: OfferPageActions.setDuration, duration })
    onClose?.()
  }, [dispatch, onClose, checkIn, checkOut])

  return (
    <Group direction="vertical">
      {durationOptions.map(option => {
        const hotelPrice = option?.price ?? 0
        const propertyFees = option?.propertyFees ?? 0
        const {
          total,
          saleUnit,
        } = getCalendarDisplayTotalPrice({
          price: hotelPrice + propertyFees,
          adultsNumber,
          roomsNumber: rooms?.length ?? 1,
          withFlights: !!flights,
          flightPrice: 0,
          nights: duration,
          displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
        })
        return <ClickableListItem
          key={option.duration}
          title={pluralizeToString('night', option.duration)}
          selected={option.duration === duration}
          data-duration={option.duration}
          onClick={onDurationClick}
          subtitle={getSubtitleLabel(option)}
        >
          {option.hasPromo && option.price && <BodyText variant="medium" colour="neutral-two">
            From{' '}
            <BodyText as="span" variant="medium" colour="neutral-one" weight="bold">
              <FormatCurrency value={total} />
              {saleUnit && <Caption variant="medium" as="span">{saleUnit}</Caption>}
            </BodyText>
          </BodyText>}
        </ClickableListItem>
      })}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  durationOptions: generateDurations(state, appState),
}), CalendarDurationDropdown)
